if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.form = null;
        this.onFormSubmitHandler = this.onFormSubmit.bind(this);
        this.submitButton = null;
        this.onSubmitButtonClickHandler = this.onSubmitButtonClick.bind(this);
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInputs = {
          recipient: this.querySelector(`#Recipient-email-recipient-${this.dataset.sectionId}`),
          self: this.querySelector(`#Recipient-email-self-${this.dataset.sectionId}`),
        };
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.senderInput = this.querySelector(`#Recipient-sender-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        this.giftcardImageInput = this.querySelector(`#Giftcard-image-property-${this.dataset.sectionId}`);
        this.customRecipientNameInput = this.querySelector(`#Recipient-custom-name-${this.dataset.sectionId}`);
        this.customRecipientEmailInput = this.querySelector(`#Recipient-custom-email-${this.dataset.sectionId}`);
        this.customRecipientMessageInput = this.querySelector(`#Recipient-custom-message-${this.dataset.sectionId}`);
        this.giftcardTemplatePayload = this.parseGiftCardTemplatePayload();
        this.modeButtons = Array.from(this.querySelectorAll('[data-recipient-mode-trigger]'));
        this.modePanels = Array.from(this.querySelectorAll('[data-recipient-mode-panel]'));
        this.activeMode = this.dataset.activeMode || 'recipient';
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = null;
        this.errorMessageList = null;
        this.errorMessage = null;
        this.defaultErrorHeader = '';
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.setActiveMode(this.activeMode, { clearErrors: false });
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.form = this.closest('form');
        if (this.form) {
          this.form.addEventListener('submit', this.onFormSubmitHandler);
          this.submitButton = this.form.querySelector('[type="submit"]');
          if (this.submitButton) {
            this.submitButton.addEventListener('click', this.onSubmitButtonClickHandler, true);
          }
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
            if (this.giftcardTemplatePayload) {
              this.giftcardTemplatePayload.initialVariant = event.data.variant;
            }
            this.syncGiftCardImage(event.data.variant);
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });

        this.syncGiftCardImage();
        this.syncCustomProperties();
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.onFormSubmitHandler);
        }
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this.onSubmitButtonClickHandler, true);
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onSubmitButtonClick(event) {
        this.syncSelectedVariantState();
        this.syncCustomProperties();
        const { isValid, errors, firstInvalidField } = this.validateRecipientForm();

        if (isValid) return true;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.clearErrorMessage();
        this.displayErrorMessage('', errors);
        if (firstInvalidField) firstInvalidField.focus();
        return false;
      }

      isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      validateRecipientForm() {
        if (this.checkboxInput.checked === false) return { isValid: true, errors: {}, firstInvalidField: null };

        const errors = {};
        let firstInvalidField = null;
        const activeEmailInput = this.activeEmailInput();
        const validations = [
          {
            key: 'name',
            field: this.nameInput,
            isValid: Boolean(this.nameInput?.value.trim()),
            message: 'Veuillez renseigner le nom du destinataire',
          },
          {
            key: 'sender',
            field: this.senderInput,
            isValid: Boolean(this.senderInput?.value.trim()),
            message: 'Veuillez renseigner l\'expéditeur',
          },
          {
            key: 'email',
            field: activeEmailInput,
            isValid: Boolean(activeEmailInput?.value.trim()),
            message:
              this.activeMode === 'self'
                ? 'Veuillez renseigner votre adresse e-mail'
                : 'Veuillez renseigner l\'email du destinataire',
          },
        ];

        validations.forEach(({ key, field, isValid, message }) => {
          if (isValid || !field || field.disabled) return;
          errors[key] = [message];
          if (!firstInvalidField) firstInvalidField = field;
        });

        if (
          activeEmailInput &&
          !activeEmailInput.disabled &&
          activeEmailInput.value.trim() &&
          !this.isValidEmail(activeEmailInput.value.trim())
        ) {
          errors.email = [
            this.activeMode === 'self'
              ? 'Veuillez renseigner une adresse e-mail valide'
              : 'Veuillez renseigner un email destinataire valide',
          ];
          if (!firstInvalidField) firstInvalidField = activeEmailInput;
        }

        return {
          isValid: Object.keys(errors).length === 0,
          errors,
          firstInvalidField,
        };
      }

      onFormSubmit(event) {
        this.syncSelectedVariantState();
        this.syncCustomProperties();
        const { isValid, errors, firstInvalidField } = this.validateRecipientForm();

        if (isValid) return true;

        event.preventDefault();
        event.stopPropagation();
        this.clearErrorMessage();
        this.displayErrorMessage('', errors);
        if (firstInvalidField) firstInvalidField.focus();
        return false;
      }

      onClick(event) {
        const modeTrigger = event.target.closest('[data-recipient-mode-trigger]');
        if (!modeTrigger || !this.contains(modeTrigger)) return;

        event.preventDefault();
        this.setActiveMode(modeTrigger.dataset.recipientModeTrigger);
      }

      onInput(event) {
        if (!event.target.closest('.recipient-fields')) return;
        this.syncCustomProperties();
      }

      onChange(event) {
        if (this.checkboxInput.checked) {
          this.enableRecipientFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearEditableFields();
          this.disableEditableFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }

        if (event?.target?.closest('variant-selects')) {
          const resolvedVariant = this.syncSelectedVariantState();
          this.syncGiftCardImage(resolvedVariant);
        }

        this.syncCustomProperties();
      }

      parseGiftCardTemplatePayload() {
        const payloadNode = this.querySelector('[data-giftcard-template-data]');
        if (!payloadNode) return null;

        try {
          return JSON.parse(payloadNode.textContent);
        } catch (error) {
          console.error('Gift card template payload could not be parsed.', error);
          return null;
        }
      }

      normalizeGiftCardTemplateText(value = '') {
        return value
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');
      }

      getTemplateOptionName() {
        return this.giftcardTemplatePayload?.templateOptionName || '';
      }

      getSelectedTemplateValue() {
        const templateOptionName = this.getTemplateOptionName();
        if (!templateOptionName) return '';

        const selectedRadio = this.querySelector(`.gift-card-modal__variant-picker input[type="radio"]:checked`);
        if (selectedRadio) return selectedRadio.value;

        const selectedDropdown = this.querySelector(`.gift-card-modal__variant-picker select[name="options[${templateOptionName}]"]`);
        if (selectedDropdown) return selectedDropdown.value;

        return '';
      }

      getCurrentVariant(variant) {
        if (variant) return variant;
        return this.giftcardTemplatePayload?.initialVariant || null;
      }

      getVariantIdInput() {
        return this.form?.querySelector('[name="id"]') || null;
      }

      getSelectedOptionValues() {
        const optionNames = Array.isArray(this.giftcardTemplatePayload?.optionNames)
          ? this.giftcardTemplatePayload.optionNames
          : [];

        if (!optionNames.length) return [];

        const formData = this.form ? new FormData(this.form) : null;
        const selectedOptionValues = optionNames
          .map((optionName) => {
            const inputName = `options[${optionName}]`;
            const submittedValue = formData?.get(inputName);

            if (typeof submittedValue === 'string' && submittedValue.trim() !== '') {
              return submittedValue.trim();
            }

            const associatedInput = this.findAssociatedOptionInput(inputName);
            if (!associatedInput) return '';

            if (associatedInput.tagName === 'SELECT') {
              return associatedInput.value?.trim() || '';
            }

            if (associatedInput.type === 'radio' || associatedInput.type === 'checkbox') {
              return associatedInput.value?.trim() || '';
            }

            return associatedInput.value?.trim() || '';
          })
          .filter(Boolean);

        return selectedOptionValues;
      }

      findAssociatedOptionInput(inputName) {
        if (!this.form) return null;

        const escapedInputName = window.CSS?.escape ? CSS.escape(inputName) : inputName.replace(/"/g, '\\"');
        const formId = this.form.id;
        const selectors = [
          `${this.tagName.toLowerCase()} [name="${escapedInputName}"]`,
          `variant-selects [name="${escapedInputName}"][form="${formId}"]`,
          `select[name="${escapedInputName}"][form="${formId}"]`,
          `input[name="${escapedInputName}"][form="${formId}"]:checked`,
          `input[name="${escapedInputName}"]:checked`,
        ];

        for (const selector of selectors) {
          const element = document.querySelector(selector);
          if (element) return element;
        }

        return null;
      }

      resolveVariantFromSelectedOptions() {
        const variants = Array.isArray(this.giftcardTemplatePayload?.variants) ? this.giftcardTemplatePayload.variants : [];
        const selectedOptionValues = this.getSelectedOptionValues();

        if (!variants.length || !selectedOptionValues.length) {
          return this.getCurrentVariant();
        }

        const matchedVariant = variants.find((variant) => {
          if (!Array.isArray(variant.options) || variant.options.length !== selectedOptionValues.length) return false;
          return variant.options.every((optionValue, index) => optionValue === selectedOptionValues[index]);
        });

        return matchedVariant || this.getCurrentVariant();
      }

      syncSelectedVariantState() {
        if (!this.giftcardTemplatePayload) return this.getCurrentVariant();

        const resolvedVariant = this.resolveVariantFromSelectedOptions();
        const variantIdInput = this.getVariantIdInput();

        if (resolvedVariant?.id) {
          this.currentProductVariantId = resolvedVariant.id.toString();
          this.giftcardTemplatePayload.initialVariant = resolvedVariant;

          if (variantIdInput && variantIdInput.value !== this.currentProductVariantId) {
            variantIdInput.value = this.currentProductVariantId;
            variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        return resolvedVariant;
      }

      resolveGiftCardTemplateByValue(templateValue) {
        const templates = Array.isArray(this.giftcardTemplatePayload?.templates) ? this.giftcardTemplatePayload.templates : [];
        const normalizedTemplateValue = this.normalizeGiftCardTemplateText(templateValue);
        if (!templates.length || !normalizedTemplateValue) return null;

        let bestMatch = null;

        templates.forEach((template, index) => {
          const normalizedTemplateTitle = this.normalizeGiftCardTemplateText(template.title);
          if (!normalizedTemplateTitle) return;

          let score = 0;
          if (normalizedTemplateTitle === normalizedTemplateValue) {
            score = 1000;
          } else if (
            normalizedTemplateTitle.includes(normalizedTemplateValue) ||
            normalizedTemplateValue.includes(normalizedTemplateTitle)
          ) {
            score = 700;
          }

          if (!score) return;

          const match = {
            ...template,
            score,
            specificity: normalizedTemplateTitle.length,
            index,
          };

          if (
            !bestMatch ||
            match.score > bestMatch.score ||
            (match.score === bestMatch.score && match.specificity > bestMatch.specificity) ||
            (match.score === bestMatch.score && match.specificity === bestMatch.specificity && match.index < bestMatch.index)
          ) {
            bestMatch = match;
          }
        });

        return bestMatch;
      }

      syncGiftCardPreview(matchedTemplate) {
        const previewContainer = this.querySelector('[data-giftcard-preview]');
        const previewImage = previewContainer?.querySelector('[data-giftcard-preview-image]');
        if (!previewContainer || !previewImage) return;

        const resolvedImageUrl = matchedTemplate?.imageUrl || this.giftcardTemplatePayload?.fallbackImageUrl || '';
        const resolvedAlt =
          matchedTemplate?.alt ||
          matchedTemplate?.title ||
          this.giftcardTemplatePayload?.fallbackAlt ||
          this.giftcardTemplatePayload?.productTitle ||
          '';

        if (!resolvedImageUrl) {
          previewContainer.hidden = true;
          previewImage.alt = '';
          return;
        }

        previewImage.src = resolvedImageUrl;
        previewImage.alt = resolvedAlt;
        previewContainer.hidden = false;
      }

      syncGiftCardImage(variant) {
        if (!this.giftcardImageInput || !this.giftcardTemplatePayload) return;

        const resolvedVariant = variant || this.syncSelectedVariantState();
        const selectedTemplateValue = this.getSelectedTemplateValue();
        const matchedTemplate = this.resolveGiftCardTemplateByValue(selectedTemplateValue);
        const resolvedImageUrl = matchedTemplate?.imageUrl || this.giftcardTemplatePayload?.fallbackImageUrl || '';

        this.giftcardImageInput.value = resolvedImageUrl;
        this.syncGiftCardPreview(matchedTemplate);

        if (resolvedVariant?.id) {
          this.currentProductVariantId = resolvedVariant.id.toString();
        }
      }

      syncCustomProperties() {
        if (this.customRecipientNameInput) {
          this.customRecipientNameInput.value = this.checkboxInput.checked ? this.nameInput?.value.trim() || '' : '';
        }

        if (this.customRecipientEmailInput) {
          this.customRecipientEmailInput.value = this.checkboxInput.checked ? this.activeEmailInput()?.value.trim() || '' : '';
        }

        if (this.customRecipientMessageInput) {
          this.customRecipientMessageInput.value = this.checkboxInput.checked ? this.messageInput?.value || '' : '';
        }
      }

      activeEmailInput() {
        return this.emailInputs[this.activeMode] || this.emailInputs.recipient || null;
      }

      recipientInputFields(mode = this.activeMode) {
        const inputs = [this.emailInputs[mode]];
        if (mode === 'recipient') inputs.push(this.sendonInput);
        return inputs.filter(Boolean);
      }

      personalizationInputFields() {
        return [this.nameInput, this.senderInput, this.messageInput].filter(Boolean);
      }

      modeSpecificFields() {
        return [...Object.values(this.emailInputs), this.sendonInput].filter(Boolean);
      }

      disableEditableFields() {
        [...this.personalizationInputFields(), ...this.modeSpecificFields()].forEach((field) => (field.disabled = true));
        if (this.offsetProperty) this.offsetProperty.disabled = true;
      }

      enableRecipientFields() {
        this.personalizationInputFields().forEach((field) => (field.disabled = false));
        this.modeSpecificFields().forEach((field) => (field.disabled = true));
        this.recipientInputFields().forEach((field) => (field.disabled = false));
        if (this.offsetProperty) this.offsetProperty.disabled = this.activeMode !== 'recipient';
      }

      clearEditableFields() {
        [...this.personalizationInputFields(), ...this.modeSpecificFields()].forEach((field) => (field.value = ''));
      }

      setActiveMode(mode, { clearErrors = true } = {}) {
        this.activeMode = mode === 'self' ? 'self' : 'recipient';
        this.dataset.activeMode = this.activeMode;

        this.modeButtons.forEach((button) => {
          const isActive = button.dataset.recipientModeTrigger === this.activeMode;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive);
          button.tabIndex = isActive ? 0 : -1;
        });

        this.modePanels.forEach((panel) => {
          panel.hidden = panel.dataset.recipientModePanel !== this.activeMode;
        });

        if (this.checkboxInput.checked) {
          this.enableRecipientFields();
        }

        this.syncCustomProperties();

        if (clearErrors) this.clearErrorMessage();
      }

      getFieldElementByKey(key) {
        const keyMap = {
          email: this.activeEmailInput(),
          name: this.nameInput,
          sender: this.senderInput,
          message: this.messageInput,
          send_on: this.sendonInput,
        };

        return keyMap[key];
      }

      getFieldSelectorByKey(key) {
        const keyMap = {
          email: this.activeEmailInput() ? `#${this.activeEmailInput().id}` : null,
          name: `#Recipient-name-${this.dataset.sectionId}`,
          sender: `#Recipient-sender-${this.dataset.sectionId}`,
          message: `#Recipient-message-${this.dataset.sectionId}`,
          send_on: `#Recipient-send-on-${this.dataset.sectionId}`,
        };

        return keyMap[key];
      }

      getFieldErrorMessageElement(key) {
        if (key === 'email') {
          return this.querySelector(
            `[data-recipient-mode-panel="${this.activeMode}"] [data-recipient-field-error="email"]`
          );
        }

        return this.querySelector(`[data-recipient-field-error="${key}"]`);
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          if (this.errorMessage) this.errorMessage.innerText = title || this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            if (key === 'send_on') this.setActiveMode('recipient', { clearErrors: false });

            const fieldSelector = this.getFieldSelectorByKey(key);
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.getFieldErrorMessageElement(key);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            const errorMessageId = errorMessageElement.id;

            if (this.errorMessageList && fieldSelector) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this.getFieldElementByKey(key);
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        if (this.errorMessage) this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [...Object.values(this.emailInputs), this.nameInput, this.senderInput, this.messageInput, this.sendonInput].filter(Boolean).forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        const keepExpanded = this.classList.contains('recipient-form--expanded');

        if (this.checkboxInput.checked) {
          if (!keepExpanded) {
            this.checkboxInput.checked = false;
          }
          this.clearEditableFields();
          this.clearErrorMessage();
          this.syncCustomProperties();

          if (keepExpanded) {
            this.enableRecipientFields();
          }
        }
      }
    }
  );
}
